<template>
  <el-collapse
      v-model="activeCollapseName"
      @change="collapseChange">
    <div v-for="(fku) in fkus"
        :key="imageKey(fku)"
        class="collapse">
      <el-collapse-item
          ref="imageInfo"
          :title="imageTitle(fku)"
          :name="imageKey(fku)">
        <draggable tag="ul"
            class="imageUl"
            :draggable="[ define.MODE.EDIT, define.MODE.NEW ].includes(mode) ? '.imageRow' : ''"
            :options="{handle: '.item-handle'}"
            @sort="onSort(imageColorCode(fku), fku, $event)">
          <li v-for="(image) in fku.images.filter((image) => image.isActive !== 0 || image.isNew === true)"
              :key="image.id"
              class="flex-box imageRow">
            <ProductImage
                :ref="`${imageColorCode(fku)}-images`"
                :image="image"
                :product-code="product.code"
                :color-code="imageColorCode(fku)"
                class="item-handle imageDiv"
                :file-data-list="fileDataList" />
            <BaseTableDetail
                :table-set-list="tableSetList"
                :display-item="image"
                :editable="editable"
                validate-scope="imageInfo" />
          </li>
        </draggable>
        <el-card v-if="showUploadBtn"
            class="box-card">
          <el-button
              type="primary"
              @click="uploadButtonClick(imageColorCode(fku))">
            画像アップロード
          </el-button>
        </el-card>
      </el-collapse-item>
      <!--  Dropzone Dialog -->
      <el-dialog
          :width="'70%'"
          :title="imageTitle(fku)"
          :visible.sync="dropzoneDialogVisible[imageColorCode(fku)]"
          @open="onOpen(imageColorCode(fku))">
        <DropImage
            :ref="`${imageColorCode(fku)}-dropZone`"
            :images="images(imageColorCode(fku))"
            :image-list="imageList(imageColorCode(fku))"
            :file-data-list="fileDataList" />
      </el-dialog>
    </div>
  </el-collapse>
</template>
<style scoped>
.collapse .el-collapse-item {
  margin-bottom: 20px;
}

.imageUl {
  padding: 0;
}

.imageUl li {
    border-bottom: 1px solid #c7d3d9;
}

.imageUl li:nth-child(2n+0) {
    background-color: #f2fbfc;
}

.imageUl > :first-child {
  border-style: solid;
  border-width: thin;
  border-color: #ade2fa;
}

.imageUl > li  {
  border-top-style: none;
  border-left-style: solid;
  border-right-style: solid;
  border-bottom-style: solid;
  border-width: thin;
  border-color: #ade2fa;
}

.imageDiv {
  min-width: 200px;
  width: 200px;
}

.imageRow .el-collapse-item table:not(.order-detail-items) th{
    width: 150px;
    padding-right: 0;
}

</style>

<script>
import _ from 'lodash'
import { mapState } from 'vuex'

import * as define from '@/define'
import common from '@/utils/common'

import DropImage from '@/components/pages/product/DropImage'
import ProductImage from '@/components/pages/product/ProductImage'
import BaseTableDetail from '@/components/molecules/BaseTableDetail'

import { ELEMENT_TYPE } from '@/components/molecules/BaseCell'
import utilProduct from '@/utils/product'
import Draggable from 'vuedraggable'

const EDITABLE = Object.freeze({
  [define.MODE.NEW]: Object.freeze({
    comment: true, imageType: true, attibutes: true
  }),
  [define.MODE.EDIT]: Object.freeze({
    comment: true, imageType: true, attibutes: true
  })
})

export default {
  name: 'ProductImages',
  components: {
    DropImage,
    Draggable,
    BaseTableDetail,
    ProductImage
  },
  props: {
    product: {
      type: Object,
      default: () => ({})
    },
    mode: {
      type: Number,
      default: define.MODE.NORMAL
    },
    originData: {
      type: Object,
      default: () => ({})
    },
    storePath: {
      type: String,
      required: true
    },
    fileDataList: {
      type: Array,
      default: () => []
    }
  },
  data () {
    return {
      activeCollapseName: [],
      uploadParam: {},
      dropzoneDialogVisible: {},
      editImageInfoDialogVisible: false,
      dialogImage: null
    }
  },
  computed: {
    define: () => define,
    ...mapState({
      customizeMaster: (state) => state.Product.customizeMaster
    }),
    editable () {
      // 編集 かつ 既に注文がある場合、サービスサイトで設定されている項目は編集不可にする
      return (EDITABLE[this.mode] || {}) || false
    },
    showUploadBtn () {
      // 編集 かつ 既に注文がある場合、サービスサイトで設定されている項目は編集不可にする
      return [ define.MODE.EDIT, define.MODE.NEW ].includes(this.mode)
    },
    isBundle () {
      return [define.PRODUCT_CATEGORY.BUNDLE, define.PRODUCT_CATEGORY.SET].includes(this.product.productCategory)
    },
    activeCollapseKey () {
      return 'productDetailCollapse'
    },
    fkus: function () {
      const sortedFkus = _.cloneDeep(((this.product || {}).fkus || []).filter((fku) => fku.isActive !== 0)) || []
      sortedFkus.sort((a, b) => {
        a.mainColor = false
        b.mainColor = false
        if (a.color.code === this.product.mainColorCode) {
          a.mainColor = true
          return -1
        } else if (b.color.code === this.product.mainColorCode) {
          b.mainColor = true
          return 1
        }

        return 0
      })

      const returnValue = sortedFkus.map((sortedFku) => {
        return this.product.fkus.find((fku) => (fku.color || {}).code === (sortedFku.color || {}).code)
      })

      if (!this.isBundle) {
        // 品番用画像用
        returnValue.unshift({
          isProduct: true,
          images: this.product.images || []
        })
      }

      return returnValue
    },
    tableSetList () {
      const returnValue = [
        {
          label: '説明',
          properties: [ {
            type: 'String', path: 'comment', elementType: ELEMENT_TYPE.INPUT_TEXT_AREA,
            editableId: 'comment', rows: 4, class: 'multiLineTextBox', lineBreak: true, editable: true,
            funcId: define.PermissionKeys.Product.Detail.Image.DESCRIPTION
          }]
        },
        {
          label: '画像種類',
          properties: [ {
            type: 'String', elementType: ELEMENT_TYPE.SELECT_BOX,
            path: 'imageType', editableId: 'imageType',
            validateRule: 'required', validateAs: '画像種類',
            codeName: 'm_product_image.image_type_options',
            selectCode: 'm_product_image.image_type_options',
            funcId: define.PermissionKeys.Product.Detail.Image.TYPE
          }]
        }
      ]

      // カスタマイズ属性を設定
      if (this.customizeMaster != null &&
        this.$isFunctionShown(define.PermissionKeys.Product.Detail.Image.ATTRIBUTE)) {
        this.customizeMaster.filter((element) => {
          // 画像属性である
          return (element.functionKey === define.CUSTOMIZE_PROPERTY_KEY.PRODUCT.IMAGE)
        }).sort((a, b) => {
          return a.displayOrder - b.displayOrder
        }).forEach((customizeObj) => {
          // 新規登録時に必須のカスタマイズ属性しかないため、権限制御しない（権限制御する場合は必須項目がある場合の権限設定の考慮が必要）
          returnValue.push(utilProduct.getCustomizeColumn(this.product, customizeObj, 'attributes', 'attribute', this.storePath, 'ProductDetail/productDetail'))
        })
      }

      return returnValue
    },
    activeImageCollapseNames: function () {
      let colorCodes = _.map(this.fkus, 'color.code')
      colorCodes.unshift('default')
      return colorCodes
    },
    images: function (colorCode) {
      return function (colorCode) {
        return this.$refs[`${colorCode}-images`]
      }
    },
    imageList: function () {
      return function (colorCode) {
        if (colorCode === '') {
          return this.product.images
        }

        return this.product.fkus.find((fku) => fku.color.code === colorCode).images
      }
    }
  },
  created () {
    this.activeCollapseName = _.split(common.getLocalStorage(this.activeCollapseKey), ',')
  },
  methods: {
    imageKey (fku) {
      return fku.isProduct ? '' : fku.color.code
    },
    imageTitle (fku) {
      if (this.isBundle || fku.isProduct) {
        return `品番：${this.product.code}`
      }
      return `${fku.mainColor ? '代表カラー ： ' : ''}${fku.color.name}`
    },
    imageColorCode (fku) {
      if (fku.isProduct) {
        return ''
      }
      return fku.color.code
    },
    collapseChange: function () {
      common.setLocalStorage(this.activeCollapseKey, this.activeCollapseName.join())
      this.activeCollapseName = common.getLocalStorage(this.activeCollapseKey).split(',')
    },
    onSort: function (colorCode, fku, event) {
      setTimeout(() => {
        this.$refs[`${colorCode}-images`].sort((a, b) => {
          return a.$el.offsetTop > b.$el.offsetTop ? 1 : -1
        }).forEach((image, index) => {
          let images = []
          if (fku.isProduct) {
            images = this.product.images
          } else {
            const fku = this.product.fkus.find((fku) => fku.color.code === colorCode)
            images = fku.images
          }

          const target = images.find((dataImage) => dataImage.id === image.image.id)
          _.set(target, 'displayOrder', index + 1)
        })
      }, 2)
    },
    uploadButtonClick: function (colorCode) {
      this.$set(this.dropzoneDialogVisible, colorCode, true)
      const dropzone = this.$refs[`${colorCode}-dropZone`]
      if (dropzone) {
        // dropzone[0].loadImage()
      }
    },
    onOpen: function (colorCode) {
      const dropzone = this.$refs[`${colorCode}-dropZone`]
      if (dropzone) {
        dropzone[0].loadImage()
      }
    }
  }
}
</script>
